import { createBrowserUuid } from '@/shared/lib/helpers';

export type OrderAttempt = Readonly<{
  orderId: string;
  expectedVersion: number;
  idempotencyKey: string;
}>;

export type IdempotencyKeyFactory = () => string;

export class OrderInputError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'OrderInputError';
  }
}

export const defaultIdempotencyKeyFactory: IdempotencyKeyFactory = () => {
  const id = createBrowserUuid();
  if (id === null) {
    throw new OrderInputError('Браузер не поддерживает безопасные ключи повторной отправки');
  }

  return id;
};

/**
 * Идемпотентная попытка изменения заказа: `orderId` + optimistic `expectedVersion`
 * + стабильный ключ повторной отправки. `expectedVersion >= 1` — версия Order
 * стартует с 1 (единый контракт с бэкендом `SettleOrderData`/`RefundOrderData`).
 */
export const createOrderAttempt = (
  orderId: string,
  expectedVersion: number,
  createIdempotencyKey: IdempotencyKeyFactory = defaultIdempotencyKeyFactory,
): OrderAttempt => {
  if (!orderId) {
    throw new OrderInputError('Не указан идентификатор записи или покупки');
  }
  if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 1) {
    throw new OrderInputError('Некорректная версия данных');
  }

  const idempotencyKey = createIdempotencyKey();
  if (!idempotencyKey) {
    throw new OrderInputError('Не удалось создать ключ повторной отправки');
  }

  return Object.freeze({ orderId, expectedVersion, idempotencyKey });
};

/** Обновляет optimistic version после явного refresh, сохраняя idempotency key. */
export const withOrderAttemptVersion = (attempt: OrderAttempt, expectedVersion: number): OrderAttempt => {
  if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 1) {
    throw new OrderInputError('Некорректная версия данных');
  }

  return Object.freeze({ ...attempt, expectedVersion });
};

/**
 * Пересоздаёт попытку после неудачной фискализации.
 *
 * Prepare идемпотентен по ключу: с прежним `idempotencyKey` он вернёт ту же
 * FAILED-операцию, а start её уже не примет — повторить оплату или возврат
 * без нового ключа невозможно.
 */
export const restartOrderAttempt = (
  order: { id: string; version: number },
  previous: OrderAttempt | null,
  createIdempotencyKey: IdempotencyKeyFactory = defaultIdempotencyKeyFactory,
): OrderAttempt => {
  const next = createOrderAttempt(order.id, order.version, createIdempotencyKey);

  console.warn('[order-attempt.restart]', {
    orderId: order.id,
    version: order.version,
    previousIdempotencyKey: previous?.idempotencyKey ?? null,
    idempotencyKey: next.idempotencyKey,
  });

  return next;
};
